import { useCallback } from 'react'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import { Button } from '@mui/material'
import { useSnackbar } from '@ui/snackbar'
import {
  parseAssemblyFromClipboardText,
  preparePastedAssembly,
  USAGE_TYPE_GERMAN_NAME,
} from '../utils/clipboardAssembly'
import { areUsageTypesCompatible } from '../utils/duplicateAssembly'

interface Props {
  usageType: AssemblyTypes
  onPaste: (assembly: Assembly) => void
}

const PasteAssemblyFromClipboardButton = ({ usageType, onPaste }: Props) => {
  const { enqueueSnackbar } = useSnackbar()

  const handlePaste = useCallback(async () => {
    try {
      const assemblyData = await navigator.clipboard.readText()
      const assembly = parseAssemblyFromClipboardText(assemblyData)

      if (!assemblyData.trim()) {
        enqueueSnackbar('Kein Aufbau in der Zwischenablage', { variant: 'warning' })
        return
      }

      if (!assembly) {
        enqueueSnackbar('Fehler beim Einfügen des Aufbaus', { variant: 'error' })
        return
      }

      if (!areUsageTypesCompatible(assembly.usage_type, usageType)) {
        const sourceName = USAGE_TYPE_GERMAN_NAME[assembly.usage_type] || assembly.usage_type
        const targetName = USAGE_TYPE_GERMAN_NAME[usageType] || usageType
        enqueueSnackbar(
          `Aufbau kann nicht zwischen ${sourceName} und ${targetName} kopiert werden. Nur kompatible Typen können kopiert werden (Wände ↔ Wände, Dach → Dach, Decke → Decke).`,
          { variant: 'error' },
        )
        return
      }

      onPaste(preparePastedAssembly(assembly))
      enqueueSnackbar('Aufbau eingefügt', { variant: 'success' })
    } catch {
      enqueueSnackbar('Fehler beim Einfügen des Aufbaus', { variant: 'error' })
    }
  }, [enqueueSnackbar, onPaste, usageType])

  return (
    <Button
      startIcon={<ContentPasteIcon />}
      onClick={handlePaste}
      variant="outlined"
      size="small"
      data-cy={`${usageType}-paste-from-clipboard`}
    >
      Aufbau aus Zwischenablage einfügen
    </Button>
  )
}

export default PasteAssemblyFromClipboardButton
